import type * as React from "react";
import { Box, Icon } from "@salestrak/ui";
import type { Omittable, RoundedToken } from "@salestrak/ui";

/**
 * The pressable avatar on a settings surface: the visual itself is the
 * button, and hovering (or focusing, or holding the picker open) reveals a
 * scrim with an edit glyph across the whole tile — the settings-avatar edit
 * affordance. Deliberately NOT `revealOnTouch`: a permanent scrim would hide
 * the very photo/color being edited; on touch the tile simply opens the
 * picker (the scrim shows for the tap that arms it, nothing more).
 */
export function AvatarEditTrigger({
  label,
  shape = "circle",
  rounded,
  children,
  ...triggerProps
}: Omittable<{
  /** Accessible name for the button ("Set avatar", …). */
  label: string;
  /** Matches the scrim's corners to the visual: user avatars are circles,
   *  the workspace tile is the 32px square's rounded-md. */
  shape?: "circle" | "square";
  /** A square tile whose corner outgrows `md` — the 64px `xl` plate rounds
   *  at `lg` — names its radius here so the scrim's corners stay flush with
   *  the visual's. Circles ignore it. */
  rounded?: RoundedToken;
  children: React.ReactNode;
}> &
  // This renders as a picker's `trigger`, and Base UI's `render` delivers
  // the popover's own props (onClick, aria-haspopup, ref, …) as component
  // props — they must reach the button element or the picker never opens.
  React.ComponentPropsWithRef<"button">) {
  return (
    <Box<"button">
      {...triggerProps}
      aria-label={label}
      as="button"
      cursor="pointer"
      group="avatar"
      position="relative"
      type="button"
    >
      {children}
      <Box
        align="center"
        hoverReveal="avatar"
        justify="center"
        position="absolute"
        rounded={shape === "circle" ? "full" : (rounded ?? "md")}
        // A fixed dark scrim with white ink, not a themed veil: it overlays
        // photos and key-color fills alike, so it cannot read from a surface.
        className="inset-0 bg-black/45 text-white"
      >
        <Icon name="pencil" size="sm" />
      </Box>
    </Box>
  );
}
